import { CompletionData, CompletionDataExecutionTypeEnum, ExerciseConfigExecutionTypeEnum, TrainingHistory } from '@/generated';
import { PrimaryValueIcon } from '@/components/PrimaryValueIcon';
import { FitnessCenter } from '@mui/icons-material';
import { Box, MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { DotsItemSymbolProps } from '@nivo/core';
import { Point, PointTooltipProps, ResponsiveLine } from '@nivo/line';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { ExerciseCard } from './ExerciseCard';
import { useAllExercisesQuery, useDeckExercisesQuery } from '@/hooks/query-hooks';
import { isOlderThanNMonths } from '@/utils/date.utils';

type ExerciseStat = {
  reps: number;
  duration: number;
  volume: number;
  date: string;
  deckId: string;
  entryId: string;
};

type Metric = 'reps' | 'duration' | 'volume';
type LineData = Array<{ id: Metric; data: Array<{ x: string; y: number; entryId: string }> }>;

const metricUnits: Record<Metric, string> = {
  reps: 'repetitions',
  duration: 'seconds',
  volume: 'volume',
};

const executionTypeLabels: Record<ExerciseConfigExecutionTypeEnum, string> = {
  [ExerciseConfigExecutionTypeEnum.Reps]: 'Reps',
  [ExerciseConfigExecutionTypeEnum.Duration]: 'Duration',
  [ExerciseConfigExecutionTypeEnum.Intervals]: 'Intervals',
};

const CustomPointSymbol = (
  props: DotsItemSymbolProps<
    Point<{
      id: string;
      data: Array<{ x: string; y: number }>;
    }>
  > & { selectedItemId?: string },
) => {
  const isSelected = props.selectedItemId === (props.datum as any)?.entryId;
  return <circle cx={1} cy={2} r={6} fill={isSelected ? 'red' : props.borderColor} stroke={props.borderColor} strokeWidth={isSelected ? 3 : 1} />;
};

const MetricLineTooltip = (
  props: PointTooltipProps<{ id: string; data: { x: string; y: number; entryId?: string }[] }> & {
    metric: Metric;
    label?: string;
    executionType?: ExerciseConfigExecutionTypeEnum;
  },
) => {
  const { data, color } = props.point;
  const executionTypeLabel = props.executionType ? executionTypeLabels[props.executionType] : undefined;
  const unit = props.executionType === ExerciseConfigExecutionTypeEnum.Reps ? 'reps' : 'seconds';
  return (
    <Paper elevation={6} sx={{ width: 'max-content', maxWidth: 'none', px: 1.25, py: 1, border: 1, borderColor: color }}>
      {props.metric === 'volume' && props.executionType ? (
        <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center', flexWrap: 'nowrap', whiteSpace: 'nowrap', '& .MuiSvgIcon-root': { fontSize: 16 } }}>
          <Typography variant="body2" sx={{ lineHeight: 1 }}>
            {executionTypeLabel} volume:
          </Typography>
          <PrimaryValueIcon executionType={props.executionType} />
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}>
            ×
          </Box>
          <FitnessCenter />
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}>
            = {data.y}
          </Box>
        </Stack>
      ) : props.executionType ? (
        <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center', flexWrap: 'nowrap', whiteSpace: 'nowrap', '& .MuiSvgIcon-root': { fontSize: 16 } }}>
          <PrimaryValueIcon executionType={props.executionType} />
          <Typography variant="body2" sx={{ lineHeight: 1 }}>
            {executionTypeLabel}: {data.y} {unit}
          </Typography>
        </Stack>
      ) : (
        <Typography variant="body2">
          {props.label ?? metricUnits[props.metric]}: {data.y}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        {moment(data.x).format('YYYY.MM.DD HH:mm')}
      </Typography>
    </Paper>
  );
};

const ExerciseMetricChart = (props: {
  data: LineData;
  metric: Metric;
  label?: string;
  executionType?: ExerciseConfigExecutionTypeEnum;
  ticks: number[];
  selectedHistoryEntryId?: string;
  setSelectedHistoryEntryId: (entryId?: string) => void;
}) => {
  const legendLabel = props.metric === 'reps' ? 'Reps' : props.metric === 'duration' ? 'Seconds' : (props.label ?? 'Volume');

  return (
  <Paper variant="outlined" sx={{ width: 900, p: 1 }}>
    <Box sx={{ height: 300, width: '100%' }}>
      <ResponsiveLine
      data={props.data.map((series) => ({ ...series, id: legendLabel }))}
      pointSymbol={(pointProps) => <CustomPointSymbol {...pointProps} selectedItemId={props.selectedHistoryEntryId} />}
      tooltip={(tooltipProps) => <MetricLineTooltip {...tooltipProps} metric={props.metric} label={props.label} executionType={props.executionType} />}
      margin={{ top: 50, right: 110, bottom: 80, left: 60 }}
      xScale={{ type: 'time', format: '%Y-%m-%dT%H:%M:%S.%LZ', precision: 'minute' }}
      yScale={{ type: 'linear', min: 0, max: 'auto', stacked: false, reverse: false }}
      axisBottom={{
        tickValues: props.ticks,
        tickPadding: 12,
        tickRotation: 45,
        format: '%d %b',
        legend: 'Days',
        legendOffset: 60,
        legendPosition: 'middle',
      }}
      colors={{ scheme: 'dark2' }}
      gridYValues={1}
      axisLeft={{ legend: legendLabel, legendOffset: -40, tickValues: 4 }}
      pointSize={10}
      pointColor={{ theme: 'background' }}
      pointBorderWidth={2}
      pointBorderColor={{ from: 'seriesColor' }}
      pointLabelYOffset={-12}
      enableTouchCrosshair
      useMesh
      onClick={(point) => props.setSelectedHistoryEntryId((point as any).data?.entryId)}
      theme={{ text: { fill: 'white' } }}
      legends={[
        {
          anchor: 'bottom-right',
          direction: 'column',
          translateX: 100,
          itemWidth: 80,
          itemHeight: 22,
          symbolShape: 'circle',
        },
      ]}
      />
    </Box>
  </Paper>
  );
};

export const ValueAndWeightPerExercise = (props: { trainingHistory?: TrainingHistory }) => {
  const { exercises } = useAllExercisesQuery(true);
  const [selectedEntry, setSelectedEntry] = useState<string>();
  const [repsData, setRepsData] = useState<LineData>([]);
  const [durationData, setDurationData] = useState<LineData>([]);
  const [volumeData, setVolumeData] = useState<LineData>([]);
  const [ticks, setTicks] = useState<number[]>([]);
  const [range, setRange] = useState<number>(1);
  const [selectedHistoryEntryId, setSelectedHistoryEntryId] = useState<string>();
  const exerciseMap = useMemo(() => buildExerciseMap(props.trainingHistory), [props.trainingHistory]);
  const sortedExerciseIds = useMemo(
    () =>
      Array.from(exerciseMap.keys()).sort((exerciseIdA, exerciseIdB) => {
        const exerciseNameA = exercises?.find((exercise) => exercise.id === exerciseIdA)?.name ?? '';
        const exerciseNameB = exercises?.find((exercise) => exercise.id === exerciseIdB)?.name ?? '';
        return exerciseNameA.localeCompare(exerciseNameB, undefined, { sensitivity: 'base' });
      }),
    [exerciseMap, exercises],
  );

  const selectedHistoryEntry = props.trainingHistory?.historyEntries.find((entry) => entry.entryId === selectedHistoryEntryId);
  const { exercises: selectedDeckExercises } = useDeckExercisesQuery(selectedHistoryEntry?.deckId);
  const currentExercise =
    selectedDeckExercises?.find((exercise) => exercise.id === selectedEntry) ?? exercises?.find((exercise) => exercise.id === selectedEntry);
  const volumeLabel = currentExercise?.config.executionType === ExerciseConfigExecutionTypeEnum.Reps ? 'Volume (reps × weight)' : 'Volume (duration × weight)';
  const selectedRoundMetrics = selectedHistoryEntry?.completedTraining.rounds.flatMap((round, roundIndex) =>
    round.exercises
      .filter((exercise) => exercise?.exerciseId === selectedEntry)
      .map((exercise) => ({
        round: roundIndex + 1,
        reps: completedReps(exercise!.completionData),
        duration: completedDuration(exercise!.completionData),
        weight: completedWeight(exercise!.completionData),
      })),
  );

  const selectEntry = (entry: ExerciseStat[], key: string) => {
    setSelectedEntry(key);
    setSelectedHistoryEntryId(undefined);
    const filtered = entry.filter((stat) => !isOlderThanNMonths(stat.date, range));
    const buildMetricData = (metric: Metric): LineData =>
      filtered.some((stat) => stat[metric] > 0) ? [{ id: metric, data: filtered.map((stat) => ({ x: stat.date, y: stat[metric], entryId: stat.entryId })) }] : [];

    setRepsData(buildMetricData('reps'));
    setDurationData(buildMetricData('duration'));
    setVolumeData(buildMetricData('volume'));
    setTicks(filtered.map((stat) => new Date(stat.date).getTime()));
  };

  useEffect(() => {
    if (selectedEntry === undefined && sortedExerciseIds.length > 0 && exercises) {
      const key = sortedExerciseIds[0];
      const entry = exerciseMap.get(key);
      if (entry) selectEntry(entry, key);
    }
  }, [exerciseMap, exercises, sortedExerciseIds]);

  useEffect(() => {
    if (selectedEntry) {
      const entry = exerciseMap.get(selectedEntry);
      if (entry) selectEntry(entry, selectedEntry);
    }
  }, [range]);

  return (
    <Box>
      <Stack direction="row" sx={{ gap: 3, alignItems: 'flex-start' }}>
        <Stack sx={{ alignItems: 'center', marginTop: 3 }} gap={2}>
          {currentExercise && <ExerciseCard timeout={5} exercise={currentExercise} />}

          <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 1, width: '100%' }}>
            <Select value={range} onChange={(event) => setRange(event.target.value)}>
              {[1, 2, 4, 6, 8, 12].map((value) => (
                <MenuItem key={value} value={value}>
                  Last {value} {value === 1 ? 'month' : 'months'}
                </MenuItem>
              ))}
            </Select>
            {selectedEntry && (
              <Select
                value={selectedEntry}
                onChange={(event) => {
                  const entry = exerciseMap.get(event.target.value);
                  if (entry) selectEntry(entry, event.target.value);
                }}
                sx={{ maxWidth: 200 }}
              >
                {sortedExerciseIds.map((exerciseId) => (
                  <MenuItem key={exerciseId} value={exerciseId}>
                    {exercises?.find((exercise) => exercise.id === exerciseId)?.name}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Stack>
          {selectedHistoryEntry && selectedRoundMetrics && <RoundMetricsTable trainingDate={selectedHistoryEntry.trainingDateIsoString} rows={selectedRoundMetrics} />}
        </Stack>

        <Stack sx={{ gap: 2 }}>
          {repsData.length > 0 && (
            <ExerciseMetricChart
              data={repsData}
              metric="reps"
              executionType={currentExercise?.config.executionType}
              ticks={ticks}
              selectedHistoryEntryId={selectedHistoryEntryId}
              setSelectedHistoryEntryId={setSelectedHistoryEntryId}
            />
          )}
          {durationData.length > 0 && (
            <ExerciseMetricChart
              data={durationData}
              metric="duration"
              executionType={currentExercise?.config.executionType}
              ticks={ticks}
              selectedHistoryEntryId={selectedHistoryEntryId}
              setSelectedHistoryEntryId={setSelectedHistoryEntryId}
            />
          )}
          {volumeData.length > 0 && (
            <ExerciseMetricChart
              data={volumeData}
              metric="volume"
              label={volumeLabel}
              executionType={currentExercise?.config.executionType}
              ticks={ticks}
              selectedHistoryEntryId={selectedHistoryEntryId}
              setSelectedHistoryEntryId={setSelectedHistoryEntryId}
            />
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

const RoundMetricsTable = (props: { trainingDate: string; rows: Array<{ round: number; weight: number; reps: number; duration: number }> }) => (
  <Box sx={{ width: '100%', mt: 1 }}>
    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
      Rounds from {moment(props.trainingDate).format('DD.MM.YYYY HH:mm')}
    </Typography>
    <TableContainer component={Paper}>
      <Table size="small" aria-label="Exercise metrics by round">
        <TableHead>
          <TableRow>
            <TableCell>Round</TableCell>
            <TableCell align="right">Reps</TableCell>
            <TableCell align="right">Duration</TableCell>
            <TableCell align="right">Weight</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.rows.map((row, index) => (
            <TableRow key={`${row.round}-${index}`}>
              <TableCell>{row.round}</TableCell>
              <TableCell align="right">{row.reps || '-'}</TableCell>
              <TableCell align="right">{row.duration || '-'}</TableCell>
              <TableCell align="right">{row.weight || '-'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

const buildExerciseMap = (trainingHistory?: TrainingHistory) => {
  const exerciseMap = new Map<string, ExerciseStat[]>();

  trainingHistory?.historyEntries.forEach((historyEntry) => {
    historyEntry.completedTraining.rounds.forEach((round) => {
      round.exercises.forEach((exercise) => {
        if (!exercise) return;

        const stats = exerciseMap.get(exercise.exerciseId) ?? [];
        const existingDateEntry = stats.find((stat) => stat.date === historyEntry.trainingDateIsoString);
        const reps = completedReps(exercise.completionData);
        const duration = completedDuration(exercise.completionData);
        const volume = completedVolume(exercise.completionData);

        if (existingDateEntry) {
          existingDateEntry.reps += reps;
          existingDateEntry.duration += duration;
          existingDateEntry.volume += volume;
        } else {
          stats.push({
            deckId: historyEntry.deckId,
            entryId: historyEntry.entryId,
            date: historyEntry.trainingDateIsoString,
            reps,
            duration,
            volume,
          });
          exerciseMap.set(exercise.exerciseId, stats);
        }
      });
    });
  });

  return exerciseMap;
};

const completedReps = (completionData: CompletionData) =>
  completionData.executionType === CompletionDataExecutionTypeEnum.Reps
    ? (completionData.completedPrimaryValue ?? 0) + (completionData.completedSecondaryValue ?? 0)
    : 0;

const completedDuration = (completionData: CompletionData) => {
  if (completionData.executionType === CompletionDataExecutionTypeEnum.Duration) {
    return (completionData.completedPrimaryValue ?? 0) + (completionData.completedSecondaryValue ?? 0);
  }

  if (completionData.executionType === CompletionDataExecutionTypeEnum.Intervals) {
    return (completionData.completedIntervals ?? []).reduce((total, interval) => total + interval.completedPrimaryTime + (interval.completedSecondaryTime ?? 0), 0);
  }

  return 0;
};

const completedWeight = (completionData: CompletionData) =>
  (completionData.completedPrimaryWeight ?? 0) + (completionData.completedSecondaryWeight ?? 0);

const completedVolume = (completionData: CompletionData) => {
  if (completionData.executionType === CompletionDataExecutionTypeEnum.Intervals) {
    return (completionData.completedIntervals ?? []).reduce(
      (total, interval) =>
        total +
        interval.completedPrimaryTime * (completionData.completedPrimaryWeight ?? 0) +
        (interval.completedSecondaryTime ?? 0) * (completionData.completedSecondaryWeight ?? 0),
      0,
    );
  }

  return (
    (completionData.completedPrimaryValue ?? 0) * (completionData.completedPrimaryWeight ?? 0) +
    (completionData.completedSecondaryValue ?? 0) * (completionData.completedSecondaryWeight ?? 0)
  );
};
